import { useMemo, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { Alert, Box, Button, Chip, FormControl, IconButton, InputLabel, Menu, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import CustomTable from '../../../../Dashboard/Components/CustomTable';
import { sortRows } from '../../../../../utilities/tableSort';
import { JOB_STAGE_FILTER_OPTIONS, JOB_STAGE_FILTERS, JOB_TABLE_HEADER, STATUS_COLORS } from './constants';
import {
	canRetryJob,
	canShowCancelAction,
	formatDateTime,
	getDisplayUrl,
	getJobDisplayStage,
	isCancellingJob,
	getProgressValue,
	getStageChipColor,
	getStageLabel,
	isCancelBlocked,
	matchesJobStageFilter,
	renderValue
} from './helpers';

const JobsTable = ({ jobs, loading, onRefresh, onOpenJobDetails, onCopyUrl, onJobAction, isFeatureEnabled }) => {
	const displayJobs = useMemo(() => jobs.map((job) => (
		loading.jobActionId === `cancel:${job.id}` ? { ...job, cancelRequested: true } : job
	)), [jobs, loading.jobActionId]);

	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const [activeJobId, setActiveJobId] = useState(null);
	const [stageFilter, setStageFilter] = useState(JOB_STAGE_FILTERS.ALL);
	const [orderBy, setOrderBy] = useState('lastExecutedAt');
	const [order, setOrder] = useState('desc');

	const getJobSortValue = (job) => {
		if (orderBy === 'progress') {
			return getProgressValue(job.progress);
		}

		if (!job[orderBy]) {
			return null;
		}

		const timestamp = new Date(job[orderBy]).getTime();
		return Number.isNaN(timestamp) ? null : timestamp;
	};

	const tableRows = useMemo(() => {
		const filteredRows = displayJobs
			.filter((job) => matchesJobStageFilter(getJobDisplayStage(job), stageFilter))
			.map((job) => ({
				...job,
				jobId: job.id,
				searchText: [job.id, job.pageURL, job.compositionId, job.language].filter(Boolean).join(' ')
			}));

		return sortRows(filteredRows, getJobSortValue, order);
	}, [displayJobs, stageFilter, orderBy, order]);

	const handleRequestSort = (column) => {
		if (column === orderBy) {
			setOrder((currentOrder) => (currentOrder === 'asc' ? 'desc' : 'asc'));
			return;
		}

		setOrderBy(column);
		setOrder('asc');
	};

	const handleOpenActionsMenu = (event, jobId) => {
		event.stopPropagation();
		setMenuAnchorEl(event.currentTarget);
		setActiveJobId(jobId);
	};

	const handleCloseActionsMenu = () => {
		setMenuAnchorEl(null);
		setActiveJobId(null);
	};

	const handleJobMenuAction = (event, jobId, action) => {
		handleCloseActionsMenu();
		onJobAction(event, jobId, action);
	};

	const renderJobRow = (job) => {
		const progressValue = getProgressValue(job.progress);
		const displayStage = getJobDisplayStage(job);

		return [
			{ key: 'jobId', content: job.id },
			{
				key: 'stage',
				content: <Chip label={getStageLabel(displayStage)} color={getStageChipColor(displayStage)} size="small"
					sx={isCancellingJob(job) ? { backgroundColor: STATUS_COLORS.cancelling, color: '#fff' } : undefined}
					variant={`${displayStage === "queued" ? "outlined" : "filled"}`} />
			},
			{ key: 'createdAt', content: formatDateTime(job.createdAt) },
			{ key: 'lastExecutedAt', content: formatDateTime(job.lastExecutedAt) },
			{ key: 'progress', content: progressValue !== null ? `${progressValue}%` : '-' },
			{
				key: 'pageURL',
				sx: { width: 220, maxWidth: 220 },
				content: job.pageURL ? (
					<Tooltip title={job.pageURL}>
						<Typography variant="body2" noWrap>
							{getDisplayUrl(job.pageURL)}
						</Typography>
					</Tooltip>
				) : (
					renderValue(job.pageURL)
				)
			},
			{
				key: 'actions',
				content: (
					<IconButton
						size="small"
						onClick={(event) => handleOpenActionsMenu(event, job.id)}
						aria-label={`Open actions for job ${job.id}`}
					>
						<MoreVertIcon fontSize="small" />
					</IconButton>
				)
			}
		];
	};

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 2,
					mb: 2,
					flexWrap: 'wrap'
				}}
			>
				<Box>
					<Typography variant="h6" fontWeight={700}>
						Jobs
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Shows all the completed, failed and pending jobs.
					</Typography>
				</Box>
				<Button variant="outlined" onClick={onRefresh} disabled={loading.jobs}>
					Refresh Jobs
				</Button>
			</Box>

			{!loading.jobs && jobs.length === 0 ? (
				<Alert severity="info">No jobs found for this site.</Alert>
			) : (
				<CustomTable
					rows={tableRows}
					rowsLoaded={!loading.jobs}
					header={JOB_TABLE_HEADER}
					rowsPerPageInit={5}
					rowsPerPageOptions={[5, 10, 20]}
					handleRowClick={(jobId) => {
						const job = jobs.find((item) => item.id === jobId);
						if (job) {
							onOpenJobDetails(job);
						}
					}}
					rowIdKey="id"
					searchLabel="Search jobs by ID or URL"
					searchKeys={['id', 'pageURL', 'compositionId', 'language', 'searchText']}
					searchStyles={{ marginBottom: '1.6rem', maxWidth: 420 }}
					resetSearchOnRowsChange={false}
					orderBy={orderBy}
					order={order}
					onRequestSort={handleRequestSort}
					searchAdornment={
						<FormControl size="small" sx={{ minWidth: 180, marginLeft: 'auto' }}>
							<InputLabel id="rss-video-stage-filter-label">Stage</InputLabel>
							<Select
								labelId="rss-video-stage-filter-label"
								value={stageFilter}
								label="Stage"
								onChange={(event) => setStageFilter(event.target.value)}
								sx={{ padding: '1rem' }}
							>
								{JOB_STAGE_FILTER_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					}
					renderRowCells={renderJobRow}
				/>
			)}

			{/* Job Actions Menu */}
			<Menu
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={handleCloseActionsMenu}
				onClick={(event) => event.stopPropagation()}
			>
				{(() => {
					const activeJob = displayJobs.find((job) => job.id === activeJobId);

					if (!activeJob) {
						return null;
					}

					return [
						canShowCancelAction(activeJob) ? (
							<MenuItem
								key="cancel"
								onClick={(event) => handleJobMenuAction(event, activeJob.id, 'cancel')}
								disabled={isCancelBlocked(activeJob) || loading.jobActionId === `cancel:${activeJob.id}`}
							>
								<CancelOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
								{isCancellingJob(activeJob) ? 'Cancelling…' : 'Cancel'}
							</MenuItem>
						) : null,
						canRetryJob(activeJob) ? (
							<MenuItem
								key="retry"
								onClick={(event) => handleJobMenuAction(event, activeJob.id, 'retry')}
								disabled={!isFeatureEnabled || loading.jobActionId === `retry:${activeJob.id}`}
							>
								<ReplayOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
								Retry
							</MenuItem>
						) : null,
						<MenuItem
							key="delete"
							onClick={(event) => handleJobMenuAction(event, activeJob.id, 'delete')}
							disabled={loading.jobActionId === `delete:${activeJob.id}`}
							sx={{ color: 'error.main' }}
						>
							<DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
							Delete
						</MenuItem>
					].filter(Boolean);
				})()}
			</Menu>
		</>
	);
};

export default JobsTable;
